import type { SelectedHeatmapEvent } from '@repo/store/maps/slices/mapData';
import type * as MapLibre from 'maplibre-gl';
import type { MapLayerMouseEvent, Map as MapLibreMap } from 'maplibre-gl';
import { MARKER_HOVER_LEAVE_DELAY_MS, SELECTED_MARKER_SOURCE_ID } from './constants';
import { allMarkerLayerIds, setHoveredMarkerOverlay } from './layers';

export function clearSelectedPinSource(map: MapLibreMap): void {
    const selectedMarkerSource = map.getSource(SELECTED_MARKER_SOURCE_ID) as MapLibre.GeoJSONSource | undefined;
    if (!selectedMarkerSource) return;
    try {
        selectedMarkerSource.setData({ type: 'FeatureCollection', features: [] });
    } catch {
        // Source mid-update.
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has('location')) {
        url.searchParams.delete('location');
        window.history.replaceState({}, '', url.toString());
    }
}

function parseVideoIds(raw: unknown): string[] | undefined {
    let parsed: unknown = raw;
    if (typeof raw === 'string' && raw.length > 0) {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return undefined;
        }
    }
    if (!Array.isArray(parsed)) return undefined;
    const ids = parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
    return ids.length > 0 ? ids : undefined;
}

function parseHeatmapSelection(feature: MapLibre.MapGeoJSONFeature): SelectedHeatmapEvent | null {
    if (feature.geometry.type !== 'Point') return null;
    const eventType = feature.properties?.event_type;
    const featureId = feature.properties?.feature_id ?? feature.id;
    if (typeof eventType !== 'string' || (typeof featureId !== 'string' && typeof featureId !== 'number')) {
        return null;
    }

    const coords = feature.geometry.coordinates;
    if (typeof coords[0] !== 'number' || typeof coords[1] !== 'number') return null;

    const videoIds = parseVideoIds(feature.properties?.video_ids);

    return {
        id: String(featureId),
        eventType,
        coordinates: [coords[0], coords[1]],
        ...(videoIds ? { videoIds } : {}),
    };
}

export function attachMarkerInteractions(
    map: MapLibreMap,
    eventTypes: readonly string[],
    popup: MapLibre.Popup,
    getLabel: (eventType: string) => string | undefined,
    onSelect: (selection: SelectedHeatmapEvent) => void,
): () => void {
    const layerIds = allMarkerLayerIds(eventTypes);
    let hoveredId: string | null = null;
    let leaveTimeout: ReturnType<typeof setTimeout> | null = null;

    const clearLeaveTimeout = () => {
        if (leaveTimeout == null) return;
        clearTimeout(leaveTimeout);
        leaveTimeout = null;
    };

    const clearHover = () => {
        clearLeaveTimeout();
        if (!hoveredId) return;
        hoveredId = null;
        setHoveredMarkerOverlay(map, null);
        map.getCanvas().style.cursor = '';
        popup.remove();
    };

    const onEnter = (e: MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        if (!feature || feature.geometry.type !== 'Point') return;
        const eventType = feature.properties?.event_type;
        const featureId = feature.properties?.feature_id ?? feature.id;
        if (typeof eventType !== 'string' || (typeof featureId !== 'string' && typeof featureId !== 'number')) {
            return;
        }
        const label = getLabel(eventType);
        if (!label) return;

        clearLeaveTimeout();
        const nextId = String(featureId);
        if (hoveredId !== nextId) {
            hoveredId = nextId;
            // Overlay layer grows the marker without setLayoutProperty on base layers
            // (which re-layouts every symbol and flashes markers).
            setHoveredMarkerOverlay(map, hoveredId);
        }
        map.getCanvas().style.cursor = 'pointer';

        const coords = feature.geometry.coordinates.slice() as [number, number];
        const el = document.createElement('div');
        el.textContent = label;
        el.style.cssText =
            'background:rgba(23,23,23,0.92);color:#fff;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:500;white-space:nowrap;backdrop-filter:blur(8px);';
        popup.setLngLat(coords).setDOMContent(el).addTo(map);
    };

    const onLeave = () => {
        clearLeaveTimeout();
        leaveTimeout = setTimeout(() => {
            leaveTimeout = null;
            clearHover();
        }, MARKER_HOVER_LEAVE_DELAY_MS);
    };

    const onClick = (e: MapLayerMouseEvent) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const selection = parseHeatmapSelection(feature);
        if (!selection) return;
        e.originalEvent.stopPropagation();
        clearHover();
        onSelect(selection);
    };

    for (const layerId of layerIds) {
        map.on('mouseenter', layerId, onEnter);
        map.on('mouseleave', layerId, onLeave);
        map.on('click', layerId, onClick);
    }

    return () => {
        for (const layerId of layerIds) {
            map.off('mouseenter', layerId, onEnter);
            map.off('mouseleave', layerId, onLeave);
            map.off('click', layerId, onClick);
        }
        clearHover();
    };
}
